// https://base-ui.com/react/components/popover
import {
  Popover as BasePopover,
  type PopoverPopupProps,
  type PopoverPositionerProps,
  type PopoverRootChangeEventDetails,
} from "@base-ui/react/popover";
import { Children, isValidElement, useState, type CSSProperties, type ReactNode } from "react";
import cx from "ui/cx";
import { PopoverContainerContext, usePopoverContainer } from "ui/hooks/usePopoverContainer";
import IconClose from "ui/icons/IconClose";
import { childrenOrFallback, promoteChildren } from "ui/promoteChildren";
import styles from "./Popover.module.scss";

/** Details forwarded by Base UI on open/close, including the `reason` for the change. */
type PopoverOpenChangeDetails = PopoverRootChangeEventDetails;

interface PopoverRootProps {
  children: ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean, eventDetails: PopoverOpenChangeDetails) => void;
  modal?: boolean;
}

// Extends Base UI's own trigger props so standard button attributes (disabled, id, aria-*, data-*,
// event handlers) reach the rendered element, matching `Tooltip.Trigger`.
interface PopoverTriggerProps
  extends Omit<React.ComponentProps<typeof BasePopover.Trigger>, "render" | "children" | "nativeButton"> {
  children: ReactNode;
  className?: string;
  /**
   * Whether the rendered trigger element is a native `<button>`. Inferred automatically for
   * intrinsic element children (e.g. `<div>` -> false); pass explicitly for custom components
   * that do not render a native button.
   */
  nativeButton?: boolean;
}

interface PopoverContentProps extends Omit<PopoverPopupProps, "className" | "render" | "style" | "ref"> {
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  side?: PopoverPositionerProps["side"];
  sideOffset?: PopoverPositionerProps["sideOffset"];
  align?: PopoverPositionerProps["align"];
  alignOffset?: PopoverPositionerProps["alignOffset"];
  collisionBoundary?: PopoverPositionerProps["collisionBoundary"];
  collisionPadding?: PopoverPositionerProps["collisionPadding"];
  arrowPadding?: PopoverPositionerProps["arrowPadding"];
  sticky?: PopoverPositionerProps["sticky"];
  /** Element/ref to position against. Defaults to the trigger. */
  anchor?: PopoverPositionerProps["anchor"];
  /**
   * Class applied to the Positioner — the `position: fixed` element that owns the popup's stacking
   * context. Use this (not `className`) to reach the popup's layer: the Popup is a static child, so
   * a z-index on `className` never escapes the Positioner's context.
   *
   * To change the layer, have the class set `--popover-z-index` rather than `z-index`. Both this
   * class and the wrapper's own `.positioner` are a single class, so two `z-index` declarations tie
   * on specificity and CSS-module insertion order decides the winner. See
   * `MultiSelect.module.scss`'s `.dropdownPositioner`.
   */
  positionerClassName?: string;
  /** Portal target. Falls back to the nearest `PopoverContainerContext`. */
  container?: HTMLElement | null;
  /** Keep the popup mounted in the DOM while closed. */
  keepMounted?: boolean;
  /**
   * Hide the popup when the anchor is scrolled out of view (Radix parity, default true).
   * Implemented via Base UI's `[data-anchor-hidden]` positioner attribute.
   */
  hideWhenDetached?: boolean;
}

interface PopoverArrowProps {
  className?: string;
  /** Retained for API compatibility; Base UI arrows are sized via CSS / the inner SVG. */
  width?: number;
  height?: number;
}

// Extends Base UI's own close props for the same reason as `PopoverTriggerProps`.
interface PopoverCloseProps
  extends Omit<React.ComponentProps<typeof BasePopover.Close>, "render" | "children" | "nativeButton"> {
  children?: ReactNode;
  className?: string;
  /** See `PopoverTriggerProps.nativeButton`. */
  nativeButton?: boolean;
}

function PopoverRoot({ children, ...props }: PopoverRootProps) {
  return <BasePopover.Root {...props}>{children}</BasePopover.Root>;
}

function PopoverTrigger({ children, className, nativeButton, ...props }: PopoverTriggerProps) {
  const { render, nativeButton: inferredNativeButton, remainingChildren } = promoteChildren(children, nativeButton);
  return (
    <BasePopover.Trigger
      className={cx(className)}
      data-testid="popover-trigger"
      nativeButton={inferredNativeButton}
      {...(render ? { render } : {})}
      {...props}
    >
      {remainingChildren}
    </BasePopover.Trigger>
  );
}

// Visual height of the SVG arrow (matches PopoverArrow's default). Radix added the arrow height to
// the offset (`mainAxis: sideOffset + arrowHeight`); Base UI's `sideOffset` is the gap to the popup
// itself, so when an arrow is rendered we add it back to keep the popup at the same distance from
// the trigger (and stop the arrow overlapping it). Only applied when a `<Popover.Arrow>` is present.
const POPOVER_ARROW_HEIGHT = 5;

function PopoverContent({
  children,
  className,
  side = "bottom",
  sideOffset = 4,
  align = "center",
  alignOffset,
  collisionBoundary,
  collisionPadding = 24,
  arrowPadding,
  sticky,
  anchor,
  positionerClassName,
  container,
  keepMounted,
  hideWhenDetached = true,
  // No default: leaving this undefined lets Base UI move focus into the popup on open. This is an
  // intentional change from the Radix-era wrapper (which always suppressed auto-focus) to standard
  // popup focus behavior; focus-sensitive consumers opt out per call site with initialFocus={false}
  // (StudioFlyoutMenu, MultiSelect).
  initialFocus,
  ...popupProps
}: PopoverContentProps) {
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(null);
  const basePopoverContainer = usePopoverContainer();
  const popoverContainer = container ?? basePopoverContainer;
  // Reserve room for the arrow only when one is actually rendered (it's an opt-in child).
  // Note: only detects a *direct* `<Popover.Arrow>` child — an arrow nested inside a wrapper
  // element won't get the offset compensation. All current consumers pass it directly.
  const hasArrow = Children.toArray(children).some((child) => isValidElement(child) && child.type === PopoverArrow);
  // `sideOffset` may be an OffsetFunction; only add the arrow height to a numeric offset.
  const positionerSideOffset =
    hasArrow && typeof sideOffset === "number" ? sideOffset + POPOVER_ARROW_HEIGHT : sideOffset;

  return (
    <BasePopover.Portal container={popoverContainer ?? undefined} keepMounted={keepMounted}>
      <BasePopover.Positioner
        side={side}
        sideOffset={positionerSideOffset}
        align={align}
        alignOffset={alignOffset}
        collisionBoundary={collisionBoundary}
        collisionPadding={collisionPadding}
        arrowPadding={arrowPadding}
        sticky={sticky}
        anchor={anchor}
        // Fixed positioning keeps the popup out of the scroll size of whatever container it
        // portals into; Base UI's default `absolute` gives the positioner large top/left values
        // that grow the container's scrollable area (page-level scrollbars in Studio flyouts).
        positionMethod="fixed"
        className={cx(styles.positioner, positionerClassName, hideWhenDetached && styles.hideWhenDetached)}
      >
        <PopoverContainerContext.Provider value={contentElement}>
          <BasePopover.Popup
            ref={setContentElement}
            className={cx(styles.content, className)}
            data-testid="popover-content"
            initialFocus={initialFocus}
            {...popupProps}
          >
            {children}
          </BasePopover.Popup>
        </PopoverContainerContext.Provider>
      </BasePopover.Positioner>
    </BasePopover.Portal>
  );
}

function PopoverArrow({ className, width = 10, height = 5, ...props }: PopoverArrowProps) {
  return (
    <BasePopover.Arrow className={cx(styles.arrow, className)} data-testid="popover-arrow" {...props}>
      <svg width={width} height={height} viewBox="0 0 30 10" preserveAspectRatio="none" className={styles.arrowSvg}>
        <polygon points="0,10 30,10 15,0" />
        {/* Stroke only the two exposed edges (not the base) so the arrow continues the popup's
            border instead of reading as a borderless triangle. */}
        <path d="M0,10 L15,0 L30,10" fill="none" vectorEffect="non-scaling-stroke" className={styles.arrowStroke} />
      </svg>
    </BasePopover.Arrow>
  );
}

function PopoverClose({ children, className, nativeButton, ...props }: PopoverCloseProps) {
  const { render, nativeButton: inferredNativeButton, remainingChildren } = promoteChildren(children, nativeButton);
  return (
    <BasePopover.Close
      className={cx(styles.close, className)}
      data-testid="popover-close"
      nativeButton={inferredNativeButton}
      {...(render ? { render } : {})}
      {...props}
    >
      {render ? undefined : childrenOrFallback(remainingChildren, <IconClose />)}
    </BasePopover.Close>
  );
}

// Compound component exports
export const Popover = {
  Root: PopoverRoot,
  Trigger: PopoverTrigger,
  Content: PopoverContent,
  Arrow: PopoverArrow,
  Close: PopoverClose,
};

// Individual component exports for flexibility
export { PopoverRoot, PopoverTrigger, PopoverContent, PopoverArrow, PopoverClose };

// Type exports
export type {
  PopoverRootProps,
  PopoverTriggerProps,
  PopoverContentProps,
  PopoverArrowProps,
  PopoverCloseProps,
  PopoverOpenChangeDetails,
};

export default Popover;
